import type { EnemyType } from '../balance';
import { NINJA_ABILITIES } from '../arenaAbilities';
import { NINJA_PASSIVE_KEYS } from '../passives';
import type { LevelDefinition } from './types';

// The kid's third sketchbook page: 14 mobs (plus the snakelet the Snake splits into)
// and 7 bosses, keeping his names and spellings. Behavior icons from the sketch map to
// kinds: arrow = charge, zigzag = weave, crosshair = ranged.
const ENEMIES: Readonly<Record<string, EnemyType>> = {
  evilChair: {
    key: 'evilChair',
    hp: 16,
    contactDmg: 5,
    speedPerSec: 55,
    radius: 9,
    xp: 1,
    color: 0x9aa2ad,
    kind: 'chase',
    sprite: 'evilChair',
  },
  mosquito: {
    key: 'mosquito',
    hp: 9,
    contactDmg: 4,
    speedPerSec: 105,
    radius: 6,
    xp: 1,
    color: 0xb07fe8,
    kind: 'weave',
    sprite: 'mosquito',
  },
  penguin: {
    key: 'penguin',
    hp: 26,
    contactDmg: 6,
    speedPerSec: 80,
    radius: 8,
    xp: 2,
    color: 0x3a4150,
    kind: 'weave',
    sprite: 'penguin',
  },
  snakelet: {
    key: 'snakelet',
    hp: 14,
    contactDmg: 5,
    speedPerSec: 95,
    radius: 6,
    xp: 1,
    color: 0x8ade6a,
    kind: 'chase',
    sprite: 'snakelet',
  },
  snake: {
    key: 'snake',
    hp: 45,
    contactDmg: 8,
    speedPerSec: 75,
    radius: 9,
    xp: 3,
    color: 0x6ad06a,
    kind: 'split',
    sprite: 'snake',
    split: { enemyKey: 'snakelet', count: 2 },
  },
  squid: {
    key: 'squid',
    hp: 70,
    contactDmg: 9,
    speedPerSec: 44,
    radius: 9,
    xp: 5,
    color: 0xf59ab8,
    kind: 'ranged',
    sprite: 'squid',
    rangedAttack: { rangePx: 470, color: 0xf59ab8, damage: 10, speed: 150, radius: 5, lifeSec: 3.6 },
  },
  evilHoover: {
    key: 'evilHoover',
    hp: 300,
    contactDmg: 16,
    speedPerSec: 50,
    radius: 14,
    xp: 12,
    color: 0x5a9ad8,
    kind: 'chase',
    sprite: 'evilHoover',
    magnetEmitter: { radiusPx: 160, pullPerSec: 100 },
  },
  cerbere: {
    key: 'cerbere',
    hp: 90,
    contactDmg: 12,
    speedPerSec: 95,
    radius: 10,
    xp: 5,
    color: 0x9c6a3f,
    kind: 'chase',
    sprite: 'cerbere',
  },
  giantSpider: {
    key: 'giantSpider',
    hp: 220,
    contactDmg: 16,
    speedPerSec: 60,
    radius: 13,
    xp: 10,
    color: 0x2f8684,
    kind: 'charge',
    sprite: 'giantSpider',
  },
  dragon: {
    key: 'dragon',
    hp: 160,
    contactDmg: 12,
    speedPerSec: 45,
    radius: 12,
    xp: 8,
    color: 0x7cc44f,
    kind: 'ranged',
    sprite: 'dragon',
    rangedAttack: { rangePx: 500, color: 0xff8a45, damage: 12, speed: 170, radius: 6, lifeSec: 3.4 },
  },
  centaurus: {
    key: 'centaurus',
    hp: 260,
    contactDmg: 18,
    speedPerSec: 66,
    radius: 13,
    xp: 12,
    color: 0x8a5c34,
    kind: 'charge',
    sprite: 'centaurus',
  },
  ciclopus: {
    key: 'ciclopus',
    hp: 420,
    contactDmg: 22,
    speedPerSec: 40,
    radius: 18,
    xp: 18,
    color: 0x58a858,
    kind: 'chase',
    sprite: 'ciclopus',
  },
  trole: {
    key: 'trole',
    hp: 380,
    contactDmg: 20,
    speedPerSec: 30,
    radius: 16,
    xp: 16,
    color: 0x4f8f4f,
    kind: 'ranged',
    sprite: 'trole',
    rangedAttack: { rangePx: 520, color: 0x9aa2ad, damage: 14, speed: 140, radius: 7, lifeSec: 4.0 },
  },
  goldEagle: {
    key: 'goldEagle',
    hp: 120,
    contactDmg: 10,
    speedPerSec: 120,
    radius: 9,
    xp: 20,
    color: 0xffd34d,
    kind: 'weave',
    sprite: 'goldEagle',
  },
  rinocerite: {
    key: 'rinocerite',
    hp: 540,
    contactDmg: 26,
    speedPerSec: 55,
    radius: 20,
    xp: 25,
    color: 0x8b9398,
    kind: 'charge',
    sprite: 'rinocerite',
  },
};

const BOSSES: Readonly<Record<string, EnemyType>> = {
  dakne: {
    key: 'dakne',
    hp: 1600,
    contactDmg: 22,
    speedPerSec: 90,
    radius: 26,
    xp: 100,
    color: 0xe0455e,
    kind: 'charge',
    sprite: 'dakne',
  },
  collector: {
    key: 'collector',
    hp: 2400,
    contactDmg: 26,
    speedPerSec: 36,
    radius: 30,
    xp: 130,
    color: 0xc4479e,
    kind: 'summon',
    sprite: 'collector',
    summon: { enemyKey: 'evilChair', count: 3, cooldownSec: 4 },
  },
  supremeVlad: {
    key: 'supremeVlad',
    hp: 3200,
    contactDmg: 30,
    speedPerSec: 60,
    radius: 28,
    xp: 160,
    color: 0x4a3a5e,
    kind: 'summon',
    sprite: 'supremeVlad',
    summon: { enemyKey: 'mosquito', count: 4, cooldownSec: 3.5 },
  },
  odin: {
    key: 'odin',
    hp: 4200,
    contactDmg: 34,
    speedPerSec: 44,
    radius: 30,
    xp: 200,
    color: 0x8cb89a,
    kind: 'ranged',
    sprite: 'odin',
    // Bosses must always come to the player - a kiting boss the kid cannot catch is a bug.
    rangedAttack: { rangePx: 560, color: 0x8cf6ff, damage: 14, speed: 200, radius: 6, lifeSec: 4.2, bounces: 1, chaseWhileFiring: true },
  },
  mosquitaro: {
    key: 'mosquitaro',
    hp: 5200,
    contactDmg: 40,
    speedPerSec: 115,
    radius: 28,
    xp: 240,
    color: 0x9a5ce8,
    kind: 'weave',
    sprite: 'mosquitaro',
  },
  ragam: {
    key: 'ragam',
    hp: 6800,
    contactDmg: 44,
    speedPerSec: 52,
    radius: 30,
    xp: 280,
    color: 0xf06292,
    kind: 'ranged',
    sprite: 'ragam',
    rangedAttack: { rangePx: 560, color: 0xf06292, damage: 16, speed: 180, radius: 7, lifeSec: 4.4, chaseWhileFiring: true },
  },
  terence: {
    key: 'terence',
    hp: 11000,
    contactDmg: 60,
    speedPerSec: 120,
    radius: 30,
    xp: 350,
    color: 0x35b0a8,
    kind: 'charge',
    sprite: 'terence',
  },
};

/** The kid's private ninja arena at /hidden/ninja-kid. Seven bosses in his sketch order,
 * one roughly every two minutes, with the final exam boss ending the run. */
export const NINJA_KID_LEVEL: LevelDefinition = {
  id: 'ninja_kid',
  displayName: 'NinjaKid Dojo',
  entitlement: 'expansion',
  runLengthSec: 900,
  musicTrack: 'ninjaKid',
  victoryBossKey: 'terence',
  schedule: [
    { t: 120, kind: 'boss', bossKey: 'dakne', banner: 'DAKNE' },
    { t: 180, kind: 'event', banner: 'NINJA SURGE', burst: 30 },
    { t: 240, kind: 'boss', bossKey: 'collector', banner: 'THE COLLECTOR' },
    { t: 360, kind: 'boss', bossKey: 'supremeVlad', banner: 'SUPREME VLAD' },
    { t: 420, kind: 'event', banner: 'SHADOW SWARM', burst: 60 },
    { t: 480, kind: 'boss', bossKey: 'odin', banner: 'ODIN' },
    { t: 600, kind: 'boss', bossKey: 'mosquitaro', banner: 'MOSQUITARO' },
    { t: 660, kind: 'event', banner: 'FINAL EXAM', burst: 90 },
    { t: 720, kind: 'boss', bossKey: 'ragam', banner: 'RAGAM' },
    { t: 900, kind: 'boss', bossKey: 'terence', banner: 'TERENCE' },
  ],
  tierUnlocks: [
    { t: 0, key: 'evilChair', w: 10 },
    { t: 0, key: 'mosquito', w: 6 },
    { t: 60, key: 'penguin', w: 6 },
    { t: 120, key: 'snake', w: 5 },
    { t: 180, key: 'squid', w: 3 },
    { t: 240, key: 'evilHoover', w: 2 },
    { t: 300, key: 'cerbere', w: 4 },
    { t: 330, key: 'giantSpider', w: 2 },
    { t: 360, key: 'dragon', w: 2 },
    { t: 420, key: 'centaurus', w: 2 },
    { t: 450, key: 'ciclopus', w: 2 },
    { t: 480, key: 'trole', w: 1 },
    { t: 510, key: 'goldEagle', w: 1 },
    { t: 540, key: 'rinocerite', w: 1 },
  ],
  enemyTypes: ENEMIES,
  bossTypes: BOSSES,
  weaponKeys: [
    'kunai', 'smokeBomb', 'canine', 'annoyHim', 'oplorous', 'distorsiun',
    'invisibility', 'wolfPack', 'sleepySmoke', 'hypnoHalo', 'cursedHowl', 'dizzyVortex',
  ],
  startingWeaponKey: 'kunai',
  passiveKeys: NINJA_PASSIVE_KEYS,
  abilities: NINJA_ABILITIES,
  spawnTuning: {
    distancePx: 720,
    distanceJitterPx: 140,
    maxActive: 600,
    maxPerTick: 10,
    baseSpawnPerSec: 2.6,
    maxSpawnPerSec: 30,
    rampToMaxSec: 480,
    hpScalePerMin: 0.1,
    dmgScalePerMin: 0.06,
  },
  difficultyOverrides: {
    easy: {
      spawnMul: 0.32,
      xpMul: 2.5,
      dmgTakenMul: 0.22,
      enemyHpMul: 0.72,
      healthOrbDropChance: 0.08,
      healthOrbHealFrac: 0.1,
    },
  },
  visualTheme: {
    backgroundColor: 0x0d1120,
    gridColor: 0x1c2338,
    groundTexture: 'stone',
    obstacleTexture: 'boulder',
    puddleTexturePrefix: 'puddle',
    foliage: 'grass',
    altarTexture: 'altar',
    playerSpritePrefix: 'ninjaPlayer',
  },
};
